import { Color, Spacing } from '@persona-id/design-tokens';
import { PlayIcon, SidebarLeftIcon, SidebarRightIcon } from '@persona-id/design-tokens/icons';
import { noop, partition } from 'lodash/fp';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import WorkflowControlPanel from 'dashboard/components/WorkflowControlPanel';
import WorkflowActionMetaProvider from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider';
import WorkflowCanvas from 'dashboard/components/WorkflowEditor/WorkflowCanvas/WorkflowCanvas';
import { WorkflowEditorContainer } from 'dashboard/components/WorkflowEditor/WorkflowEditor';
import { FlowPlotterController } from 'dashboard/components-lib/FlowPlotter';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { getLoopTokensToCycleIdx, getTokensToRunSteps } from 'dashboard/lib/workflows';
import { Environment } from 'dashboard/models/environment';
import { Workflow, WorkflowErrorType, WorkflowSchema } from 'dashboard/models/workflow';
import {
  TriggerObject,
  WORKFLOW_RUN_TERMINAL_STATES,
  WorkflowRunStatus,
} from 'dashboard/models/workflow-run';
import {
  OVERRIDABLE_REPORT_TYPES,
  SIMULTATE_OVERRIDABLE_ACTION_TYPES,
  WorkflowRunSimulateContext,
  WorkflowRunSimulated,
} from 'dashboard/models/workflow-run-simulated';
import { WorkflowRunStep, WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import {
  OUTPUT_STEP_TOKEN,
  outputStep,
  WorkflowActionRunReportGenericV1,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepInput,
  WorkflowStepType,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { useUsableObjects } from 'dashboard/redux/selectors/workflow-editor-context';
import useSimulatedWorkflowRunValidation from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/useSimulatedWorkflowRunValidation';
import WorkflowSimulateControlPanel from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/WorkflowSimulateControlPanel';
import WorkflowsSimulateEnvironmentSwitchModal from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowsSimulateEnvironmentSwitchModal';
import WorkflowRunStatusSticker from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsTable/WorkflowRunStatusSticker';
import WorkflowValidationErrorsDropdown from 'dashboard/views/DashboardWorkflowsShowView/WorkflowValidationErrorsDropdown';
import Button from 'design-system/components/Button';
import { Hairline } from 'design-system/variables/effect';
import { FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { buildGraph } from 'lib/workflows';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
`;

const ButtonWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
`;

const Toolbar = styled(FullScreenHeaderContainer)`
  height: ${Spacing[8]};
  box-shadow: ${Hairline.Top}, ${Hairline.Bottom};
  padding-left: ${Spacing[0]};
  padding-right: ${Spacing[0]};
  z-index: 0;
`;

const ToolbarHorizontalContainer = styled(HorizontalContainer)`
  justify-content: space-between;
  width: 100%;
`;

const ToolbarButtonWrapper = styled.div`
  padding: ${Spacing[2]};
  border: 1px solid ${Color.BorderHairline};
`;

interface DashboardWorkflowRunsSimulateShowViewProps {
  backRoute: string;
  handleRun: () => Promise<void>;
  knownObjects: WorkflowRunStepKnownObjects[];
  setSimulateContext: (simulateContext: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  steps: WorkflowRunStep[];
  inputStep: WorkflowStepInput;
  triggerObject: TriggerObject | null;
  workflow: Workflow;
  workflowRun: WorkflowRunSimulated;
  workflowSchema: WorkflowSchema;
  workflowVersion: WorkflowVersion;
  currentEnvironment: Environment;
  environments: Environment[];
  setAndPersistCurrentEnvironment: (currentEnvironment: Environment) => void;
  simulationTimedOut: boolean;
}

const DashboardWorkflowRunsSimulateShowView: FC<DashboardWorkflowRunsSimulateShowViewProps> = ({
  backRoute,
  handleRun,
  knownObjects,
  steps: runSteps,
  setSimulateContext,
  simulateContext,
  inputStep,
  triggerObject,
  workflow: {
    id: workflowId,
    attributes: { name: workflowName },
  },
  workflowRun,
  workflowSchema,
  workflowVersion: {
    attributes: { 'steps-config': stepsConfig, outputs },
  },
  currentEnvironment: { id: currentEnvironmentId },
  environments,
  setAndPersistCurrentEnvironment,
  simulationTimedOut,
}) => {
  const {
    attributes: { status: runStatus, 'started-at': runStartedAt },
    relationships: {
      environment: {
        data: { id: environmentIdOfRun },
      },
    },
  } = workflowRun;
  const wfrInProgress = runStatus === WorkflowRunStatus.InProgress;

  const [activeNodeToken, setActiveNodeToken] = useState<string | null>(inputStep.token);
  const [focusedNodeToken, setFocusedNodeToken] = useState<string | null>(inputStep.token);
  const [loopTokensToCycleIdx, setLoopTokensToCycleIdx] = useState<Record<string, number>>({});
  const [
    simulateControlPanelOpen,
    { disable: closeSimulatePanel, enable: openSimulatePanel, toggle: toggleSimulatePanel },
  ] = useToggle(!WORKFLOW_RUN_TERMINAL_STATES.includes(runStatus));
  const [
    triggerFieldDefinitionPanelOpen,
    {
      disable: closeTriggerFieldDefinitionPanel,
      enable: openTriggerFieldDefinitionPanel,
      toggle: toggleTriggerFieldDefinitionPanel,
    },
  ] = useToggle(false);
  const [workflowControlPanelOpen, { disable: closeControlPanel, toggle: toggleControlPanel }] =
    useToggle(false);
  const [showEnvironmentUpdateModal, { enable: openEnvironmentUpdateModal }] = useToggle();
  const [triggerFieldSchemas, setTriggerFieldSchemas] = useState<FieldSchema[]>([]);

  const tokensToSteps = useMemo(() => buildGraph(stepsConfig || []), [stepsConfig]);
  const tokensToRunSteps = useMemo(
    () => getTokensToRunSteps(loopTokensToCycleIdx, tokensToSteps, runSteps),
    [runSteps, loopTokensToCycleIdx, tokensToSteps],
  );
  const activeNode = useMemo(() => {
    if (!activeNodeToken) {
      return null;
    }
    if (activeNodeToken === outputStep.token) {
      return outputStep;
    }
    return tokensToSteps[activeNodeToken]?.step || inputStep;
  }, [activeNodeToken, tokensToSteps, inputStep]);

  const [overridableActionSteps, unoverridableSteps] = useMemo(
    () =>
      partition(
        (step): step is WorkflowStepAction => {
          if (step.type !== WorkflowStepType.Action) return false;

          //for Run Report steps, need to ensure override is supported for reporty type
          if (step.action.key === WorkflowStepActionKey.RunReportGeneric) {
            return OVERRIDABLE_REPORT_TYPES.some(
              (type) =>
                type ===
                (step.action as WorkflowActionRunReportGenericV1).params['report-step-action-key'],
            );
          }

          return SIMULTATE_OVERRIDABLE_ACTION_TYPES.some((type) => type === step.action?.key);
        },
        Object.values(tokensToSteps).flatMap((elem) => elem.step),
      ),
    [tokensToSteps],
  );

  const deactivatedNodeTokens: string[] = useMemo(() => {
    if (runStartedAt != null) return [];

    return unoverridableSteps.map(({ token }) => token).concat(OUTPUT_STEP_TOKEN);
  }, [runStartedAt, unoverridableSteps]);

  const navigate = useNavigate();

  const { validationErrors, requiredFields, referencedFields } = useSimulatedWorkflowRunValidation(
    simulateContext,
    triggerFieldSchemas,
    inputStep.trigger?.id || '',
    runSteps,
    stepsConfig,
    workflowRun.attributes,
  );

  const wfrHasConfigErrors = useMemo(
    () => validationErrors.some((error) => error.type === WorkflowErrorType.Save),
    [validationErrors],
  );

  useEffect(() => {
    currentEnvironmentId !== environmentIdOfRun && openEnvironmentUpdateModal();
  }, [currentEnvironmentId, environmentIdOfRun, openEnvironmentUpdateModal]);

  useEffect(() => {
    setLoopTokensToCycleIdx(getLoopTokensToCycleIdx(runSteps));
  }, [runSteps]);

  const updateCycleIndex = (idx: number, token: string) => {
    setLoopTokensToCycleIdx({ ...loopTokensToCycleIdx, [token]: idx });
  };

  const onClickError = (index: number) => {
    setActiveNodeToken(validationErrors[index].token);
    if ('field' in validationErrors[index]) {
      openSimulatePanel();
      openTriggerFieldDefinitionPanel();
    }
  };

  const handleSelectNode = (token: string) => {
    setFocusedNodeToken(null);
    setActiveNodeToken(token);
  };

  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();

  const setEnvironment = () => {
    const newEnv = environments.find(({ id }) => id === environmentIdOfRun);
    if (newEnv) {
      setAndPersistCurrentEnvironment(newEnv);
    }
  };

  const usableObjects = useUsableObjects(
    activeNodeToken && tokensToSteps[activeNodeToken] ? activeNodeToken : undefined,
  );

  return (
    <>
      <ModalWrapper isOpen={showEnvironmentUpdateModal} shouldCloseOnOverlayClick={false}>
        <WorkflowsSimulateEnvironmentSwitchModal
          handleCancel={() => navigate(`/dashboard/workflows/${workflowId}/runs`)}
          handleConfirm={setEnvironment}
        />
      </ModalWrapper>
      <WorkflowActionMetaProvider
        context='workflows'
        externalIntegrationVendors={externalIntegrationVendors}
      >
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBack to={backRoute} />
            {workflowName}
            <StatusWrapper>
              <WorkflowRunStatusSticker
                workflowRunStatus={runStatus}
                simulationTimedOut={simulationTimedOut}
              />
            </StatusWrapper>
          </HorizontalContainer>
          <HorizontalContainer>
            <WorkflowValidationErrorsDropdown
              validationErrors={validationErrors}
              onClickError={onClickError}
            />
            <ButtonWrapper>
              <Button
                disabled={wfrInProgress || wfrHasConfigErrors}
                id='dashboard-workflow-runs-simulate-show-view__run'
                isPending={wfrInProgress && !simulationTimedOut}
                onClick={handleRun}
                prefixIcon={<PlayIcon />}
                variant='primary'
              >
                Run
              </Button>
            </ButtonWrapper>
          </HorizontalContainer>
        </FullScreenHeaderContainer>
        <Toolbar>
          <ToolbarHorizontalContainer>
            <ToolbarButtonWrapper>
              <Button
                id='dashboard-workflow-runs-simulate-show-view__simulate-panel-hide'
                onClick={toggleSimulatePanel}
                variant={simulateControlPanelOpen ? 'basic' : 'inverse'}
              >
                <SidebarLeftIcon />
              </Button>
            </ToolbarButtonWrapper>
            <ToolbarButtonWrapper>
              <Button
                id='dashboard-workflow-runs-simulate-show-view__simulate-panel-show'
                onClick={toggleControlPanel}
                variant={workflowControlPanelOpen ? 'basic' : 'inverse'}
              >
                <SidebarRightIcon />
              </Button>
            </ToolbarButtonWrapper>
          </ToolbarHorizontalContainer>
        </Toolbar>
        <WorkflowEditorContainer isShowingHeaderStatus={false} isShowingToolbar>
          {simulateControlPanelOpen && inputStep.trigger && (
            <WorkflowSimulateControlPanel
              steps={overridableActionSteps}
              trigger={inputStep.trigger}
              activeNode={activeNode}
              setFocusedNodeToken={setFocusedNodeToken}
              setActiveNodeToken={setActiveNodeToken}
              closePanel={closeSimulatePanel}
              simulateContext={simulateContext}
              setSimulateContext={setSimulateContext}
              validationErrors={validationErrors}
              disabled={wfrInProgress && !simulationTimedOut}
              setTriggerFieldSchemas={setTriggerFieldSchemas}
              triggerFieldSchemas={triggerFieldSchemas}
              requiredFields={requiredFields}
              referencedFields={referencedFields}
              triggerFieldDefinitionPanelOpen={triggerFieldDefinitionPanelOpen}
              toggleTriggerFieldDefinitionPanel={toggleTriggerFieldDefinitionPanel}
              closeTriggerFieldDefinitionPanel={closeTriggerFieldDefinitionPanel}
            />
          )}
          <FlowPlotterController>
            <WorkflowCanvas
              deactivatedNodeTokens={deactivatedNodeTokens}
              editable={false}
              focusedNodeToken={focusedNodeToken}
              selectedNodeToken={activeNodeToken}
              erroredNodeTokens={[]}
              runLoopTokensToFocusedCycle={loopTokensToCycleIdx}
              runSteps={runStatus === WorkflowRunStatus.Pending ? null : runSteps}
              steps={stepsConfig}
              inputStep={inputStep}
              addNodeAfter={noop}
              addNodeAfterBranchStart={noop}
              moveStep={noop}
              selectNode={handleSelectNode}
            />
          </FlowPlotterController>
          {workflowControlPanelOpen && (
            <WorkflowControlPanel
              usableObjects={usableObjects}
              workflowRunStep={activeNodeToken != null ? tokensToRunSteps[activeNodeToken] : null}
              updateStep={noop}
              deleteStep={noop}
              updateInputStep={noop}
              setConditionalRouteToEdit={noop}
              stepsConfig={stepsConfig || []}
              inputStep={inputStep}
              workflowId={workflowId}
              workflowSchema={workflowSchema}
              platformObjectJsonSchema={undefined}
              workflowName={workflowName}
              editable={false}
              activeNode={activeNode}
              conditionalRouteToEdit={0}
              tokensToSteps={tokensToSteps}
              validationErrors={validationErrors}
              setFocusedNodeToken={setFocusedNodeToken}
              closePanel={closeControlPanel}
              knownObjects={knownObjects}
              triggerObject={triggerObject}
              workflowVersionOutput={outputs}
              updateWorkflowVersionOutput={noop}
              workflowRun={workflowRun}
              updateCycleIndex={updateCycleIndex}
              loopTokensToCycleIdx={loopTokensToCycleIdx}
              showRunStats={false}
            />
          )}
        </WorkflowEditorContainer>
      </WorkflowActionMetaProvider>
    </>
  );
};

export default DashboardWorkflowRunsSimulateShowView;
